/**
 * Map geometry, as the simulation sees it.
 *
 * A `MapDef` is pure data with no Three.js in it — the renderer builds meshes
 * from the same structure, but the sim is the authority on where the ground is.
 * v1's arena editor exports templates in a richer format; converting one to a
 * `MapDef` is the map pipeline's job, not the sim's.
 *
 * Everything is a box because everything a player can stand on in v1 is a box,
 * and boxes make the broadphase and the resolver both trivial and exact.
 */
import { dcos, dsin, TWO_PI } from "./math.js";
import { dist2XZ } from "./vec.js";
export function makePlatform(id, x, y, z, sizeX, sizeY, sizeZ, surface = { kind: "normal" }) {
    return {
        id,
        x,
        y,
        z,
        hx: sizeX * 0.5,
        hy: sizeY * 0.5,
        hz: sizeZ * 0.5,
        solid: true,
        surface,
    };
}
export function platformTop(p) {
    return p.y + p.hy;
}
/**
 * How far the deck reaches past the zone's opening radius. 1.5 is the number
 * `ArenaRenderer._buildFloor` was already drawing at, so adopting it here made
 * the drawn deck true rather than moving the edge to a new place.
 */
export const FLOOR_RADIUS_FACTOR = 1.5;
/**
 * The arena floor is a disc of radius `floorRadius` about the arena centre —
 * the shared contract with the renderer, whose deck mesh is exactly this disc.
 * A "solid" floor exists only where this is true; past the rim you fall, and
 * `killY` settles it. Without the cut, sprinting off the rendered deck left
 * players grounded on invisible floor hundreds of metres out.
 *
 * It is deliberately NOT `startRadius`. Cutting the deck at the zone's opening
 * radius put the lethal drop exactly on the line the zone starts at, with the
 * spawn ring only 0.28r inside it: eight bots charging each other walked off
 * the rim within seven seconds and three of eight were dead at tick 200. The
 * zone is the boundary that decides matches; the deck edge is the backstop
 * behind it.
 */
export function onArenaFloor(map, x, z) {
    return dist2XZ(x, z, map.centerX, map.centerZ) <= map.floorRadius * map.floorRadius;
}
/**
 * A plain bowl: a solid ground plane, a ring of spawns, and four raised
 * platforms so vertical play exists. Good enough to test the sim and to stand
 * in for a real map until the map pipeline lands.
 */
/**
 * The arena's radius when nothing overrides it.
 *
 * Exported because it was being duplicated: the client's `MAP_META` carried its
 * own copy, so shrinking the arena here for the fixed-camera framing left the
 * offline gym building the old 45-unit bowl — the one place the game is
 * actually playtested. A number two runtimes must agree on belongs to one of
 * them.
 */
export const DEFAULT_ARENA_RADIUS = 25;
/**
 * The five real arenas moved to `arenas.ts`, and `createArena` with them.
 *
 * This file is the map PRIMITIVES — the types, `makePlatform`, the graybox, and
 * the constants an arena is built out of. Arenas are built FROM those, so
 * keeping both here made the dependency circular the moment an arena needed to
 * place a prop. Now it runs one way.
 *
 * `createArena` is still exported from `@chaos/sim`; only the file moved.
 */
export function createFlatMap(opts) {
    const radius = opts?.radius ?? DEFAULT_ARENA_RADIUS;
    const spawnCount = opts?.playerCount ?? 8;
    const spawns = [];
    const spawnRadius = radius * 0.78;
    for (let i = 0; i < spawnCount; i++) {
        // Fixed angular steps, computed with the deterministic trig so a map built
        // on the client is bit-identical to one built on the server.
        const a = (i / spawnCount) * TWO_PI;
        // Spawned above the deck so the 5s invulnerable drop is an actual drop.
        spawns.push({ x: spawnRadius * dsin(a), y: 6, z: spawnRadius * dcos(a) });
    }
    /**
     * COVER, not scenery.
     *
     * The old graybox was five floating slabs in a 135-unit bowl: nothing to hide
     * behind, nothing to break a sightline, and 4.2 seconds of sprinting to cross
     * while a melee exchange happens inside 2.6 units. Brawl Stars arenas are
     * small and full of WALLS — the interesting decision is which side of one to
     * be on, and that decision does not exist on an open plain.
     *
     * Tall and thin on purpose. A wall you can see over is decoration; 3.5 units
     * blocks the sightline of a body 2 units tall. Thin is only safe because the
     * collision sweep landed — before it, anything under ~1.8u could be dashed
     * clean through.
     *
     * Two low steps survive so the sim's vertical paths (jump, ceiling contact,
     * the ground/air accel split) still get exercised. Both are reachable from
     * the deck: the jump apex is 4.59u and they top out at 2.5.
     */
    const wall = (id, x, z, sx, sz) => makePlatform(id, x, 1.75, z, sx, 3.5, sz);
    const r = radius;
    const platforms = opts?.bare ? [] : [
        // A broken ring of cover at mid-radius: four long walls on the diagonals,
        // gapped so every approach has both a safe line and an exposed one.
        wall("cover_ne", r * 0.42, -r * 0.42, 10, 1.6),
        wall("cover_sw", -r * 0.42, r * 0.42, 10, 1.6),
        wall("cover_nw", -r * 0.42, -r * 0.42, 1.6, 10),
        wall("cover_se", r * 0.42, r * 0.42, 1.6, 10),
        // Centre: a short cross that everyone fights around rather than over.
        wall("core_x", 0, 0, 9, 1.6),
        wall("core_z", 0, 0, 1.6, 9),
        // Outer stubs breaking the long sight lines down each axis.
        wall("edge_n", 0, -r * 0.78, 6, 1.6),
        wall("edge_s", 0, r * 0.78, 6, 1.6),
        // The two low steps. Jumpable from the deck, and something to contest.
        makePlatform("step_e", r * 0.62, 2, 0, 7, 1, 7),
        makePlatform("step_w", -r * 0.62, 2, 0, 7, 1, 7),
    ];
    return {
        id: "flat",
        name: "Graybox Bowl",
        platforms,
        windZones: [],
        spawns,
        floor: { kind: "solid", y: 0 },
        killY: -50,
        centerX: 0,
        centerZ: 0,
        startRadius: radius,
        floorRadius: radius * FLOOR_RADIUS_FACTOR,
        zoneCenterJitter: radius * 0.3,
    };
}
//# sourceMappingURL=map.js.map